import { createSlice, createAsyncThunk } from "@reduxjs/toolkit"
import { httpinstance } from "../../axios/api"

/**
 * A request that never reached the server has no `err.response` at all, so the
 * callers' `res?.payload?.data` would be undefined and the screen would sit
 * there with no message. Every thunk below falls back to a body shaped like
 * the API's own error responses, which is what the toast in App.js renders
 * (it needs `success` and `message`) and what the signup screen branches on
 * (`status`).
 */
const offlineResponse = {
    data: {
        status: false,
        success: 'error',
        statusCode: 0,
        message: 'We could not reach the server. Please check your connection and try again.',
    },
}

/**
 * A request rejected by the route's own validators answers with
 * `{ status: 406, errors: [{ msg, param }] }` and no `message` — which the
 * toast cannot render, so the screen would look like nothing happened. Lift
 * the first error into the shape everything else uses, and pass `field`
 * through as the offending input so the screen can point at it.
 */
const normalize = (response) => {
    const body = response?.data
    if (!body || body.message || !Array.isArray(body.errors) || body.errors.length === 0) {
        return response
    }
    const [first] = body.errors
    return {
        ...response,
        data: {
            ...body,
            status: false,
            success: 'error',
            message: first?.msg || 'Please check the details you entered.',
            field: first?.param || first?.path || null,
        },
    }
}

/* ------------------------------------------------------------------ */
/*  Signup OTP endpoints                                               */
/* ------------------------------------------------------------------ */

/**
 * Step 1 — details. Verifies the GST/Aadhaar document and sends the mobile and
 * email codes (and, on the Aadhaar path, triggers the Aadhaar OTP).
 *
 * Replaces `signup` (auth/register) below for the signup screen. The response
 * carries a `registration_id`, which every later call quotes instead of
 * re-posting the whole form — and no `ref_id`: the Aadhaar reference now stays
 * server-side, so the code the user types can only ever be checked against the
 * Aadhaar number this registration was started with.
 */
export const registerRequestOtp = createAsyncThunk('registerRequestOtp', async (values) => {
    try {
        const response = await httpinstance.post('auth/register/otp/request', values)
        return normalize(response)
    } catch (err) {
        return normalize(err.response) || offlineResponse
    }
})

/**
 * Re-issue ONE code: { registration_id, channel: 'sms' | 'email' | 'adhaar' }.
 *
 * The old screen had no per-code resend to call — auth/register re-sent all
 * three at once — so asking for a fresh email code also fired another SMS. An
 * Aadhaar resend additionally carries `doc_number`, because the raw Aadhaar
 * number is never stored server-side and a new provider OTP cannot be
 * triggered without it.
 */
export const registerResendOtp = createAsyncThunk('registerResendOtp', async (values) => {
    try {
        const response = await httpinstance.post('auth/register/otp/resend', values)
        return normalize(response)
    } catch (err) {
        return normalize(err.response) || offlineResponse
    }
})

/**
 * Step 2 — verify the code and create the account. The body carries only the
 * registration id and the code; the signup's own details are read from the
 * registration record on the server.
 *
 * `contact_otp` is NOT the six digits the user typed: the screen encrypts them
 * first (utils/clientCrypto.js, keyed on the registration id travelling beside
 * it), so the code never crosses the wire or lands in a captured payload in
 * the clear. The server decrypts it with the same derivation.
 */
export const registerVerifyOtp = createAsyncThunk('registerVerifyOtp', async (values) => {
    try {
        const response = await httpinstance.post('auth/register/otp/verify', values)
        return normalize(response)
    } catch (err) {
        return normalize(err.response) || offlineResponse
    }
})

/* ------------------------------------------------------------------ */
/*  Legacy signup endpoints                                            */
/* ------------------------------------------------------------------ */

/**
 * The original two-call signup (plaintext OTP storage, no per-code resend, the
 * Aadhaar ref_id round-tripping through the browser). Still mounted on the
 * API and still exported here so nothing that imports it breaks, but the
 * signup screen no longer uses either one — see the three thunks above.
 */
export const signup = createAsyncThunk('signup', async (values)=>{
    try{
        const url = 'auth/register'
        console.log(url);
        const response = await httpinstance.post(url,values)
        return response
    }catch (err){
        return err.response
    }
})
export const verifysignupotp = createAsyncThunk('verifysignupotp', async (values)=>{
    try{
        const url = 'auth/register-verify'
        const response = await httpinstance.post(url,values)
        return response
    }catch (err){
        return err.response
    }
})

const initialState = {
    signupUser : {},
}

const signupSlice = createSlice({
    name : 'signupslice',
    initialState,
    extraReducers:{
    }
})


export default signupSlice.reducer
